import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { getActivityFeed } from '../../lib/api';
import { MagnifyingGlass, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const secs = Math.floor((now - d) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function ActivityFeed({ currentUser }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getActivityFeed(200).then(setItems).catch(() => toast.error('Failed to load activity')).finally(() => setLoading(false));
  }, []);

  const canSeeBorrower = currentUser?.is_admin || ['Admin', 'AVP'].includes(currentUser?.role);
  const filtered = items.filter(i =>
    !search ||
    i.user_name.toLowerCase().includes(search.toLowerCase()) ||
    i.workflow_label.toLowerCase().includes(search.toLowerCase()) ||
    (canSeeBorrower && (i.borrower || '').toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-4" data-testid="activity-feed">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground">Activity Feed</h2>
          <p className="text-xs text-muted-foreground mt-1">Real-time log of Clara usage across the team ({filtered.length} events).</p>
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search user or workflow..." className="pl-9 h-9 w-72 border-border" data-testid="activity-search" />
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">No activity yet.</p>
          ) : (
            <div className="divide-y divide-border/50">
              {filtered.map(it => (
                <div key={it.id} className="flex items-center gap-3 p-3 hover:bg-muted/20" data-testid={`activity-${it.id}`}>
                  <span className="w-8 h-8 rounded-lg bg-[#E63935]/20 text-[#E63935] flex items-center justify-center text-[11px] font-bold flex-shrink-0">{it.user_initials}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-foreground">{it.user_name}</p>
                      <span className="text-[10px] text-muted-foreground">{it.user_role}</span>
                      <span className="text-[10px] text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">used</span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#E63935]"><Sparkle size={11} weight="fill" />{it.workflow_label}</span>
                      {canSeeBorrower && it.borrower && <span className="text-xs text-muted-foreground">for <span className="text-foreground font-medium">{it.borrower}</span></span>}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(it.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
